import { useMemo, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Trophy, Target, Repeat, Rocket, TrendingUp, Award, Lock, Sparkles } from "lucide-react";

function computeClimb(audits) {
  if (!audits || audits.length < 4) return null;
  const chrono = [...audits].reverse(); // oldest -> newest
  const n = chrono.length;
  const half = Math.floor(n / 2);
  const early = chrono.slice(0, half);
  const recent = chrono.slice(half);
  const avg = (arr) => arr.reduce((s, a) => s + a.score, 0) / arr.length;
  return Math.round(avg(recent) - avg(early));
}

export const Milestones = ({ audits }) => {
  const stats = useMemo(() => {
    const list = audits || [];
    const totalLoops = list.reduce((s, a) => s + (a.est_loops_saved || 0), 0);
    const best = list.length ? Math.max(...list.map((a) => a.score)) : 0;
    return { count: list.length, totalLoops, best, climb: computeClimb(list) };
  }, [audits]);

  const milestones = useMemo(
    () => [
      { id: "first", label: "First Audit", desc: "You ran your first audit", icon: Sparkles, done: stats.count >= 1 },
      { id: "five", label: "Getting Going", desc: "5 audits logged", icon: Rocket, done: stats.count >= 5 },
      { id: "ten", label: "Prompt Veteran", desc: "10 audits logged", icon: Award, done: stats.count >= 10 },
      { id: "green", label: "Green Zone", desc: "Scored 75+ on a prompt", icon: Target, done: stats.best >= 75 },
      { id: "rising", label: "On The Rise", desc: "Your average is climbing", icon: TrendingUp, done: stats.climb !== null && stats.climb > 0 },
      { id: "rebuild", label: "Rebuild Saver", desc: "25+ rebuild loops avoided", icon: Repeat, done: stats.totalLoops >= 25 },
    ],
    [stats]
  );

  // Celebrate newly-unlocked milestones once per browser.
  const fired = useRef(false);
  useEffect(() => {
    if (fired.current || stats.count === 0) return;
    fired.current = true;
    const key = "ct_milestones_seen";
    let seen = [];
    try {
      seen = JSON.parse(localStorage.getItem(key) || "[]");
    } catch {
      seen = [];
    }
    const doneIds = milestones.filter((m) => m.done).map((m) => m.id);
    const fresh = doneIds.filter((id) => !seen.includes(id));
    if (seen.length && fresh.length) {
      const m = milestones.find((x) => x.id === fresh[fresh.length - 1]);
      if (m) toast.success(`Milestone unlocked: ${m.label}`, { duration: 6000 });
    }
    localStorage.setItem(key, JSON.stringify(doneIds));
  }, [milestones, stats.count]);

  if (stats.count === 0) return null;

  const climb = stats.climb;
  const banner =
    climb !== null && climb > 0
      ? {
          tone: "up",
          title: `You're getting sharper — readiness up +${climb} pts.`,
          sub: "Your recent prompts score higher than your early ones. Being signed in is paying off.",
        }
      : stats.count < 4
      ? {
          tone: "neutral",
          title: `Run ${4 - stats.count} more audit${4 - stats.count === 1 ? "" : "s"} to unlock your progress trend.`,
          sub: "Coretext needs a few audits to chart how your prompts are improving.",
        }
      : {
          tone: "neutral",
          title: "Keep auditing — your average climbs as your prompts tighten.",
          sub: "Tighten a few more prompts and watch your readiness trend turn green.",
        };

  const up = banner.tone === "up";

  return (
    <section className="mb-8" data-testid="milestones">
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="glass rounded-2xl p-6 md:p-7 relative overflow-hidden"
        style={
          up
            ? { border: "1px solid rgba(34,229,138,0.4)", boxShadow: "0 0 40px rgba(34,229,138,0.12)" }
            : { border: "1px solid var(--line)" }
        }
      >
        <div className="flex items-start gap-3">
          <span
            className="inline-flex items-center justify-center w-11 h-11 rounded-xl shrink-0"
            style={{
              border: `1px solid ${up ? "#22e58a" : "#22d3ee"}80`,
              boxShadow: `0 0 18px ${up ? "#22e58a" : "#22d3ee"}45`,
            }}
          >
            <Trophy size={20} style={{ color: up ? "#22e58a" : "#22d3ee" }} />
          </span>
          <div>
            <p className="font-mono text-[11px] tracking-[0.28em] mb-1" style={{ color: up ? "#22e58a" : "var(--muted)" }}>
              YOUR PROGRESS
            </p>
            <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight text-white leading-tight" data-testid="milestone-headline">
              {banner.title}
            </h2>
            <p className="font-mono text-sm text-[var(--muted)] mt-1.5 max-w-xl leading-relaxed">{banner.sub}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 mt-6">
          {milestones.map((m, i) => (
            <motion.div
              key={m.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: m.done ? 1 : 0.5, scale: 1 }}
              transition={{ delay: 0.1 + i * 0.05 }}
              data-testid={`milestone-${m.id}`}
              className="rounded-xl p-3 text-center relative"
              style={{
                border: `1px solid ${m.done ? "rgba(34,229,138,0.4)" : "var(--line)"}`,
                background: m.done ? "rgba(34,229,138,0.05)" : "rgba(11,13,20,0.5)",
              }}
            >
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg mb-2" style={{ border: `1px solid ${m.done ? "#22e58a" : "var(--line)"}` }}>
                {m.done ? (
                  <m.icon size={17} style={{ color: "#22e58a" }} />
                ) : (
                  <Lock size={15} className="text-[var(--muted)]" />
                )}
              </span>
              <p className="font-display font-bold text-xs text-white leading-tight">{m.label}</p>
              <p className="font-mono text-[9px] text-[var(--muted)] mt-1 leading-tight">{m.desc}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};
